// EXP-6: .tracecad zip (document.json + manifest.json + assets/<hash>) via lazy fflate,
// plain-JSON both ways, and PST-6 File System Access with download/input fallback.
import type { Doc } from "../doc/entities";
import { fromJSON, toJSON, type LoadResult } from "../doc/serialize";
import type { PersistDriver } from "./driver";

const MANIFEST = { app: "tracecad", schema: 1 };

export interface ImportResult {
  result: LoadResult;
  assets: Map<string, Uint8Array>; // hash → original bytes (stored to IDB by the caller)
}

export async function exportTracecad(doc: Doc, driver: PersistDriver): Promise<Blob> {
  const { zipSync, strToU8 } = await import("fflate");
  const files: Record<string, Uint8Array> = {
    "manifest.json": strToU8(JSON.stringify({ ...MANIFEST, exportedAt: new Date().toISOString() })),
    "document.json": strToU8(JSON.stringify(toJSON(doc)))
  };
  for (const hash of Object.keys(doc.assets)) {
    const blob = await driver.getAsset(hash);
    if (blob) files[`assets/${hash}`] = new Uint8Array(await blob.arrayBuffer());
    // missing assets export as absent → EC-EXP-5 placeholders on the other side
  }
  return new Blob([zipSync(files).slice().buffer], { type: "application/zip" });
}

export async function importTracecad(bytes: Uint8Array): Promise<ImportResult> {
  const { unzipSync, strFromU8 } = await import("fflate");
  let files: Record<string, Uint8Array>;
  try {
    files = unzipSync(bytes);
  } catch {
    return { result: { ok: false, errors: ["File is damaged (not a valid .tracecad zip)."] }, assets: new Map() };
  }
  const manifestRaw = files["manifest.json"];
  const docRaw = files["document.json"];
  if (!manifestRaw || !docRaw) {
    return { result: { ok: false, errors: ["File is damaged (missing manifest or document)."] }, assets: new Map() };
  }
  try {
    const manifest = JSON.parse(strFromU8(manifestRaw)) as { app?: string; schema?: number };
    if (manifest.app !== "tracecad") {
      return { result: { ok: false, errors: ["Not a TraceCAD file."] }, assets: new Map() };
    }
  } catch {
    return { result: { ok: false, errors: ["File is damaged (bad manifest)."] }, assets: new Map() };
  }
  let json: unknown;
  try {
    json = JSON.parse(strFromU8(docRaw));
  } catch {
    return { result: { ok: false, errors: ["File is damaged (bad document JSON)."] }, assets: new Map() };
  }
  const result = fromJSON(json);
  if (!result.ok) return { result, assets: new Map() };

  // EXP-6: verify asset content hashes; a mismatch rejects the whole file.
  const assets = new Map<string, Uint8Array>();
  for (const hash of Object.keys(result.doc.assets)) {
    const data = files[`assets/${hash}`];
    if (!data) continue; // absent asset → placeholder at render time (EC-EXP-5)
    const actual = await sha256Hex(data);
    if (actual !== hash) {
      return { result: { ok: false, errors: ["File is damaged (asset checksum mismatch)."] }, assets: new Map() };
    }
    assets.set(hash, data);
  }
  return { result, assets };
}

export function exportJSON(doc: Doc): Blob {
  return new Blob([JSON.stringify(toJSON(doc), null, 2)], { type: "application/json" });
}

export function importJSON(text: string): LoadResult {
  try {
    return fromJSON(JSON.parse(text));
  } catch {
    return { ok: false, errors: ["Not valid JSON."] };
  }
}

export async function sha256Hex(data: Uint8Array): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", data.slice().buffer as ArrayBuffer);
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

// ---- PST-6 File System Access with fallbacks (EC-PST-4) --------------------------

interface FsaWindow {
  showSaveFilePicker?(opts: unknown): Promise<{ createWritable(): Promise<{ write(b: Blob): Promise<void>; close(): Promise<void> }> }>;
  showOpenFilePicker?(opts: unknown): Promise<{ getFile(): Promise<File> }[]>;
}

export async function saveBlobToFile(blob: Blob, suggestedName: string): Promise<boolean> {
  const w = window as unknown as FsaWindow;
  if (w.showSaveFilePicker) {
    try {
      const handle = await w.showSaveFilePicker({
        suggestedName,
        types: [{ description: "TraceCAD project", accept: { "application/zip": [".tracecad"] } }]
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return true;
    } catch (err) {
      if ((err as Error).name === "AbortError") return false; // user cancelled
      // fall through to download fallback
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = suggestedName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return true;
}

export async function openFileFromPicker(accept: string): Promise<File | null> {
  const w = window as unknown as FsaWindow;
  if (w.showOpenFilePicker) {
    try {
      const [handle] = await w.showOpenFilePicker({
        types: [{ description: "TraceCAD files", accept: { "application/octet-stream": accept.split(",") } }]
      });
      return handle ? await handle.getFile() : null;
    } catch (err) {
      if ((err as Error).name === "AbortError") return null;
      // fall through to input fallback
    }
  }
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.onchange = () => resolve(input.files?.[0] ?? null);
    // cancel without change event: resolve null when focus returns without a file
    window.addEventListener("focus", () => setTimeout(() => resolve(input.files?.[0] ?? null), 300), {
      once: true
    });
    input.click();
  });
}
